import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CadCheatSheetProps {
  onClose: () => void;
}

export function CadCheatSheet({ onClose }: CadCheatSheetProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Stop arrow propagation while sheet is open
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  const sections = [
    {
      title: t('projects.cadProject.cheatSheet.selectTool', 'Select Tool'),
      items: [
        {
          keys: t('projects.cadProject.cheatSheet.click', 'Click'),
          desc: t('projects.cadProject.cheatSheet.selectPolygon', 'Select polygon'),
        },
        {
          keys: t('projects.cadProject.cheatSheet.clickDrag', 'Click + Drag'),
          desc: t('projects.cadProject.cheatSheet.editPointsEdges', 'Edit polygon points / edges'),
        },
        {
          keys: t('projects.cadProject.cheatSheet.hoverVertex', 'Hover vertex'),
          desc: t('projects.cadProject.cheatSheet.highlightVertex', 'Highlight & enlarge vertex'),
        },
        {
          keys: t('projects.cadProject.cheatSheet.longPressDrag', 'Long-press + Drag'),
          desc: t('projects.cadProject.cheatSheet.movePolygon', 'Move selected polygon'),
        },
      ],
    },
    {
      title: t('projects.cadProject.cheatSheet.drawing'),
      items: [
        { keys: 'Click', desc: t('projects.cadProject.cheatSheet.placePoint') },
        { keys: 'Double-click', desc: t('projects.cadProject.cheatSheet.closePolygon') },
        { keys: 'Enter', desc: t('projects.cadProject.cheatSheet.closePolygon') },
        { keys: 'Escape', desc: t('projects.cadProject.cheatSheet.cancelDrawing') },
        { keys: '⌘/Ctrl + Z', desc: t('projects.cadProject.cheatSheet.undoPoint') },
      ],
    },
    {
      title: t('projects.cadProject.cheatSheet.circleDrawing'),
      items: [{ keys: 'Click + Drag', desc: t('projects.cadProject.cheatSheet.circleDrag') }],
    },
    {
      title: t('projects.cadProject.cheatSheet.subtractDrawing'),
      items: [
        { keys: 'Click', desc: t('projects.cadProject.cheatSheet.placePoint') },
        { keys: 'Double-click', desc: t('projects.cadProject.cheatSheet.closePolygon') },
      ],
    },
    {
      title: t('projects.cadProject.cheatSheet.subtractCircleDrawing'),
      items: [{ keys: 'Click + Drag', desc: t('projects.cadProject.cheatSheet.subtractCircleDrag') }],
    },
    {
      title: t('projects.cadProject.cheatSheet.editing', 'Editing'),
      items: [
        { keys: '⌘/Ctrl + Z', desc: t('common.undo', 'Undo') },
        { keys: '⌘/Ctrl + Shift + Z', desc: t('common.redo', 'Redo') },
        { keys: 'Escape', desc: t('projects.cadProject.cheatSheet.deselectPolygon', 'Deselect polygon') },
        { keys: 'Del / Backspace', desc: t('projects.cadProject.cheatSheet.deletePolygon', 'Delete selected polygon') },
      ],
    },
    {
      title: t('projects.cadProject.cheatSheet.navigation'),
      items: [
        { keys: t('workspace.cheatSheet.scrollWheel'), desc: t('projects.cadProject.cheatSheet.zoom') },
        {
          keys: t('projects.cadProject.cheatSheet.middleDrag', 'Middle-click + Drag'),
          desc: t('projects.cadProject.cheatSheet.pan'),
        },
        {
          keys: t('projects.cadProject.cheatSheet.rightDrag', 'Right-click + Drag'),
          desc: t('projects.cadProject.cheatSheet.pan'),
        },
        { keys: '← → ↑ ↓', desc: t('projects.cadProject.cheatSheet.panArrows') },
        { keys: 'Shift + ← → ↑ ↓', desc: t('projects.cadProject.cheatSheet.finePan') },
      ],
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200]" onClick={onClose} />

      {/* Panel — consistent with workspace CheatSheet */}
      <div className="fixed left-[90px] top-1/2 -translate-y-1/2 z-[201] bg-[#1E1E1E] border border-white/10 rounded-xl shadow-2xl w-[360px] h-[600px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-white/10 shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-white">{t('projects.cadProject.cheatSheet.title')}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.title}>
                <div className="text-xs font-bold text-[#A3E635] uppercase tracking-wider mb-2">{section.title}</div>
                <div className="space-y-1.5">
                  {section.items.map((item) => (
                    <div key={item.keys} className="flex items-center justify-between gap-3">
                      <kbd className="inline-flex items-center px-2 py-0.5 rounded bg-white/5 border border-white/10 text-xs font-mono text-gray-300 whitespace-nowrap">
                        {item.keys}
                      </kbd>
                      <span className="text-xs text-gray-400 text-right">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
